import { useCallback, useRef } from 'react';
import { api, ApiError } from '@/lib/api';

interface ReviewedQuoteRequest {
  readonly idempotencyKey: string;
  readonly endpoint: string;
  readonly body: unknown;
  readonly expectedUpdatedAt: string;
}

/** Match the JSON wire body regardless of the caller's object key order. */
function serializeBody(body: unknown): string | undefined {
  return JSON.stringify(body, (_key, value: unknown) => {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return Object.fromEntries(
        Object.entries(value).sort(([left], [right]) => left.localeCompare(right)),
      );
    }
    return value;
  });
}

/** Only a determinate refusal releases an intent for a fresh manual attempt. */
function isDefiniteRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status < 400 || error.status >= 500) return false;
  // Authentication, permission and session-lock guards run before replay (423
  // can even be thrown locally). They cannot resolve an earlier lost response.
  if ([401, 403, 423].includes(error.status)) return false;
  // A timeout or early response does not establish the original write's outcome.
  if (error.status === 408 || error.status === 425) return false;
  const data = error.data as { code?: unknown; error?: unknown; outcome?: unknown } | undefined;
  const unresolvedCodes = ['IDEMPOTENCY_IN_PROGRESS', 'IDEMPOTENCY_OUTCOME_UNKNOWN'];
  if (unresolvedCodes.includes(String(data?.code)) || unresolvedCodes.includes(String(data?.error))) return false;
  if (['unknown', 'pending', 'in_progress'].includes(String(data?.outcome))) return false;
  return true;
}

/**
 * Keep one key and frozen request per reviewed quote intent for this hook's
 * lifetime. A lost response or manual retry can replay the same acceptance or
 * rejection even after its If-Match token becomes stale. A definite refusal
 * releases that key so the same reviewed data can be submitted again after
 * a conflict is resolved. Pending and uncertain outcomes keep their key. Changed schedule,
 * quote version, or endpoint creates a new intent. The backend binds keys to
 * the authenticated tenant and principal; this state is never persisted.
 */
export function useQuoteMutationRequest() {
  const reviewedRequests = useRef(new Map<string, ReviewedQuoteRequest>());

  return useCallback(async <T,>(endpoint: string, body: unknown, expectedUpdatedAt: string): Promise<T> => {
    const serializedBody = serializeBody(body);
    const fingerprint = JSON.stringify([endpoint, expectedUpdatedAt, serializedBody]);
    let request = reviewedRequests.current.get(fingerprint);
    if (!request) {
      request = {
        idempotencyKey: crypto.randomUUID(),
        endpoint,
        body: serializedBody === undefined ? undefined : JSON.parse(serializedBody) as unknown,
        expectedUpdatedAt,
      };
      reviewedRequests.current.set(fingerprint, request);
    }
    const send = () => api.post<T>(request.endpoint, request.body, {
      headers: {
        'Idempotency-Key': request.idempotencyKey,
        'If-Match': request.expectedUpdatedAt,
      },
    });
    const sendWithRecovery = async () => {
      try {
        return await send();
      } catch (error) {
        // Fetch reports a lost connection as TypeError. The server may already
        // have committed, so recover once with the same intent and replay key.
        // A second error propagates: this never starts an endless retry loop.
        if (!(error instanceof TypeError)) throw error;
        return send();
      }
    };
    try {
      return await sendWithRecovery();
    } catch (error) {
      // The backend stores 4xx answers too. A resolved lift conflict needs a
      // new key on the next manual submit, otherwise it would replay forever.
      // A late refusal must not evict a newer request for the same fingerprint.
      if (isDefiniteRefusal(error) && reviewedRequests.current.get(fingerprint) === request) {
        reviewedRequests.current.delete(fingerprint);
      }
      throw error;
    }
  }, []);
}
